import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Crown, Eye, Info } from "@phosphor-icons/react";

import { consumerAccountsEnabled, useConsumerAuth } from "../context/ConsumerAuthContext";
import consumerApi from "../lib/consumerApi";


export const premiumLiveOddsEnabled = () => (
  consumerAccountsEnabled()
  && process.env.REACT_APP_PREMIUM_LIVE_ODDS_ENABLED === "true"
);

const observedLabel = (value) => {
  if (!value) return "Observation time unavailable";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "Observation time unavailable";
  return `Observed ${parsed.toLocaleString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  })}`;
};

export default function PremiumLiveOdds({ competitionId }) {
  const auth = useConsumerAuth();
  const enabled = premiumLiveOddsEnabled();
  const entitled = auth?.account?.subscription?.entitlement_active === true;
  const [state, setState] = useState({ status: "idle", data: null });

  useEffect(() => {
    let active = true;
    if (!enabled || !auth?.ready || !entitled || !competitionId) return undefined;
    setState({ status: "loading", data: null });
    consumerApi.get(`/premium/live-odds/${encodeURIComponent(competitionId)}`).then(
      ({ data }) => {
        if (active) setState({ status: "ready", data });
      },
      (error) => {
        if (!active) return;
        const status = error?.response?.status;
        setState({
          status: status === 401 || status === 403 ? "locked" : "unavailable",
          data: null,
        });
      },
    );
    return () => { active = false; };
  }, [auth?.ready, competitionId, enabled, entitled]);

  if (!enabled || !auth?.ready) return null;

  if (!auth.account || !entitled || state.status === "locked") {
    const signedIn = Boolean(auth.account);
    return (
      <section
        className="mt-8 border-y border-white/15 py-5 text-white"
        aria-labelledby="premium-live-odds-title"
        data-testid="premium-live-odds-locked"
      >
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="max-w-2xl">
            <div className="overline inline-flex items-center gap-2 text-[var(--cc-gold)]">
              <Crown size={14} weight="fill" /> Competition Checker Premium
            </div>
            <h2 id="premium-live-odds-title" className="mt-2 font-display text-2xl font-semibold">
              Current Live Odds
            </h2>
            <p className="mt-2 text-sm leading-relaxed text-[var(--cc-text-muted)]">
              See the latest trustworthy current-entry odds when the source and draw mechanic support them.
            </p>
          </div>
          <Link
            className="btn-gold inline-flex min-h-10 items-center rounded px-4 py-2 text-sm"
            to={signedIn ? "/account/subscription" : "/signin"}
          >
            {signedIn ? "View Premium" : "Sign in for Premium"}
          </Link>
        </div>
      </section>
    );
  }

  if (state.status === "loading" || state.status === "idle") {
    return (
      <section className="mt-8 border-y border-white/15 py-5 text-white" aria-live="polite">
        <p className="text-sm text-[var(--cc-text-muted)]">Loading current Live Odds...</p>
      </section>
    );
  }

  const available = state.status === "ready" && state.data?.status === "available";
  const reason = state.data?.unavailable_reasons?.[0]?.message
    || "The latest evidence is not sufficient for a safe Live Odds calculation.";

  return (
    <section
      className="mt-8 border-y border-white/15 py-5 text-white"
      aria-labelledby="premium-live-odds-title"
      data-testid="premium-live-odds"
    >
      <div className="overline inline-flex items-center gap-2 text-[var(--cc-gold)]">
        <Crown size={14} weight="fill" /> Competition Checker Premium
      </div>
      <div className="mt-3 flex flex-col gap-5 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 id="premium-live-odds-title" className="font-display text-2xl font-semibold">
            Current Live Odds
          </h2>
          <div className="mt-3 inline-flex items-center gap-2">
            <Eye size={20} weight="fill" className="text-[var(--cc-gold)]" />
            <strong className="font-mono-num text-3xl" data-testid="premium-live-odds-value">
              {available ? state.data.current_live_odds.display : "Live odds unavailable"}
            </strong>
          </div>
          <p className="mt-2 text-xs text-[var(--cc-text-muted)]">
            {observedLabel(state.data?.observed_at)}
          </p>
        </div>
        {available && (
          <dl className="grid min-w-[220px] grid-cols-2 gap-x-6 gap-y-2 text-sm">
            <div><dt className="text-white/50">Current entries</dt><dd className="font-mono-num">{Number(state.data.current_entries).toLocaleString("en-GB")}</dd></div>
            <div><dt className="text-white/50">Maximum entries</dt><dd className="font-mono-num">{Number(state.data.maximum_entries).toLocaleString("en-GB")}</dd></div>
          </dl>
        )}
      </div>
      <div className="mt-4 flex items-start gap-2 text-sm leading-relaxed text-[var(--cc-text-muted)]">
        <Info size={16} className="mt-0.5 shrink-0" />
        <p>{available ? state.data.disclosure : reason}</p>
      </div>
    </section>
  );
}
